import {
  WorkflowCapabilityReference,
  WorkflowDefinitionDocument,
  WorkflowDefinitionNode,
} from './platform-workflow-definition-lifecycle.models';

export type StepChecklistTarget =
  | 'actions'
  | 'responsibility'
  | 'dashboard'
  | 'detail'
  | 'paths'
  | 'outcomes';
export interface StepChecklistItem {
  id: string;
  title: string;
  status: 'configured' | 'attention' | 'inspect' | 'optional';
  detail: string;
  target: StepChecklistTarget;
  action: string;
}

/** An inventory of local settings, not a compiler or a prediction of runtime eligibility. */
export function workflowStepChecklist(
  document: WorkflowDefinitionDocument | null,
  nodeId: string | null,
): StepChecklistItem[] {
  const matches = document?.nodes.filter((node) => node?.id === nodeId) ?? [];
  if (!document || matches.length !== 1 || matches[0].typeCode !== 'human_step')
    return [];
  const node = matches[0],
    declaration = node.declaration;
  const commands = declaration?.behavior?.commands ?? [];
  const referencesRecorded =
    commands.length > 0 &&
    commands.every((command) =>
      document.capabilityReferences?.some((reference) =>
        sameReference(reference, command.capability),
      ),
    );
  const items: StepChecklistItem[] = [
    {
      id: 'purpose',
      title: 'Actions and required input',
      target: 'actions',
      action: 'Open Detail actions',
      status: referencesRecorded
        ? 'configured'
        : commands.length
          ? 'inspect'
          : 'attention',
      detail: commands.length
        ? `${commands.length} action${commands.length === 1 ? '' : 's'} attached. Inspect installed forms and module requirements in Components; saved validation checks compatibility.`
        : 'Attach installed actions to build this step. Forms and checks come from the selected modules.',
    },
  ];

  const attached = (document.assignmentPolicies ?? []).filter(
    (policy) => policy?.nodeId === nodeId,
  );
  const policy = attached.length === 1 ? attached[0] : null;
  const modes: Record<string, string> = {
    queue_only: 'Queue only',
    claim: 'Claim based',
    direct: 'Direct assignment',
    claim_or_direct: 'Claim or direct assignment',
  };
  const validAttachment =
    policy?.scopeCode === 'step' &&
    !!policy.id?.trim() &&
    (document.assignmentPolicies ?? []).filter(
      (candidate) => candidate?.id === policy.id,
    ).length === 1;
  const roleAssignment = policy?.assignmentBasisCode === 'roles';
  const policyRecorded =
    validAttachment &&
    !!policy &&
    (roleAssignment
      ? policy.assignmentModeCode !== 'queue_only' &&
        Object.hasOwn(modes, policy.assignmentModeCode) &&
        !!policy.eligibleRoleCodes?.some((role) => !!role?.trim()) &&
        !policy.initialQueueCode &&
        !policy.eligibleQueueCodes?.length
      : !!policy.initialQueueCode &&
        !!policy.eligibleQueueCodes?.includes(policy.initialQueueCode) &&
        Object.hasOwn(modes, policy.assignmentModeCode) &&
        (policy.assignmentModeCode === 'queue_only'
          ? !policy.eligibleRoleCodes?.length
          : !!policy.eligibleRoleCodes?.some((role) => !!role?.trim())));
  const referenceMatches =
    !declaration || declaration.behavior?.assignmentPolicyId === policy?.id;
  items.push({
    id: 'responsibility',
    title: 'Staff assignment',
    target: 'responsibility',
    action: 'Open Dashboard assignment',
    status:
      !attached.length && !declaration?.behavior?.assignmentPolicyId
        ? 'optional'
        : policyRecorded && referenceMatches
          ? 'configured'
          : 'attention',
    detail: !attached.length
      ? 'No step policy is attached; existing assignment behavior applies. Workflow policies do not provide step responsibility.'
      : !validAttachment
        ? 'The step attachment is ambiguous or invalid. Inspect the recorded policies.'
        : !referenceMatches
          ? 'The page settings and attached responsibility policy need to be linked.'
          : !policyRecorded
            ? roleAssignment
              ? 'Choose eligible assignment roles in Dashboard settings.'
              : 'Inspect the retained queue assignment settings or switch to role assignment in Dashboard settings.'
            : `${
                roleAssignment
                  ? policy!
                      .eligibleRoleCodes!.filter(
                        (role): role is string => !!role,
                      )
                      .map(label)
                      .join(', ')
                  : label(policy!.initialQueueCode)
              } · ${modes[policy!.assignmentModeCode]}. Live eligibility and permissions are checked when work is assigned.`,
  });

  const paths = document.edges.filter((edge) => edge?.fromNodeId === node.id);
  const next =
    paths.length === 1 ? uniqueNode(document, paths[0].toNodeId) : null;
  const typedCompletion =
    [
      '1.8',
      '1.9',
      '1.10',
      '1.11',
      '1.12',
      '1.13',
      '1.14',
      '1.15',
      '1.16',
      '1.17', '1.18', '1.19', '1.20', '1.21', '1.22',
    ].includes(document.payloadSchemaVersion) &&
    !!declaration?.behavior.completionRequirements?.length &&
    commands.filter(
      (command) =>
        command.capability.key.provider === 'workflow' &&
        command.capability.key.code === 'workflow.step.complete' &&
        command.capability.key.version === 1 &&
        document.capabilityReferences?.some((reference) =>
          sameReference(reference, command.capability),
        ),
    ).length === 1;
  const outcomes = typedCompletion
    ? []
    : commands.flatMap((command) => command.outcomes);
  const routePaths =
    next?.typeCode === 'routing_decision'
      ? document.edges.filter((edge) => edge.fromNodeId === next.id)
      : [];
  const routed =
    outcomes.length > 0 &&
    next?.typeCode === 'routing_decision' &&
    !paths[0].outcomeCode &&
    !paths[0].pathKindCode &&
    routePaths.length === outcomes.length &&
    outcomes.every((outcome) => {
      const matched = routePaths.filter(
        (edge) =>
          edge.outcomeCode === outcome.code &&
          edge.toNodeId === outcome.destinationNodeId,
      );
      return (
        matched.length === 1 &&
        !!uniqueNode(document, outcome.destinationNodeId)
      );
    });
  const direct =
    !outcomes.length &&
    next &&
    ['human_step', 'terminal'].includes(next.typeCode) &&
    !paths[0].outcomeCode &&
    !paths[0].pathKindCode;
  const routeDescription = routed
    ? outcomes
        .map((outcome) => {
          const edge = routePaths.find(
            (item) => item.outcomeCode === outcome.code,
          )!;
          return `${outcome.label} → ${name(uniqueNode(document, edge.toNodeId)!)}${edge.pathKindCode === 'return' ? ' (return visit)' : ''}`;
        })
        .join('; ') + '.'
    : direct
      ? `Next → ${name(next)}. Module destination requirements are checked by saved validation.`
      : null;
  items.push({
    id: 'destinations',
    title: 'Where completed work goes',
    status: routeDescription ? 'configured' : 'attention',
    detail:
      routeDescription ??
      'Choose destinations for the attached actions and inspect paths for ambiguity.',
    target: 'outcomes',
    action: 'Open outcome destinations',
  });

  const dashboards = declaration?.dashboards ?? [];
  const compatible =
    dashboards.length > 0 &&
    dashboards.every(
      (dashboard) =>
        dashboard.columns?.length > 0 &&
        document.capabilityReferences?.some((reference) =>
          sameReference(reference, dashboard.view),
        ),
    );
  items.push({
    id: 'dashboard',
    title: 'Queue dashboard',
    target: 'dashboard',
    action: 'Open dashboard settings',
    status: compatible
      ? 'configured'
      : dashboards.length
        ? 'inspect'
        : 'optional',
    detail: compatible
      ? 'Dashboard selections are recorded. Saved validation checks their providers and fields.'
      : dashboards.length
        ? 'Inspect the saved view references and columns.'
        : 'No dashboard is recorded. Choose an installed worklist in Dashboard.',
  });

  const detail = declaration?.detail,
    interaction = detail?.mainInteraction;
  const interactionMatches =
    !interaction ||
    (interaction.code === 'generated-command-form' &&
      interaction.version === 1 &&
      declaration?.behavior?.commands?.some((command) =>
        sameReference(command?.capability, interaction.command),
      ));
  const supportedDetail =
    commands.length > 0 &&
    detail?.layout === 'workspace.two-column.v1' &&
    ['side', 'below', 'hidden'].includes(detail.contextPlacement) &&
    Array.isArray(detail.blocks) &&
    interactionMatches;
  items.push({
    id: 'detail',
    title: 'Staff detail page',
    target: 'detail',
    action: 'Open detail workspace',
    status: supportedDetail ? 'configured' : detail ? 'inspect' : 'optional',
    detail: supportedDetail
      ? `Workspace layout recorded with ${detail.blocks.length} content block${detail.blocks.length === 1 ? '' : 's'} and ${interaction ? 'a selected' : 'the default'} task form. Provider checks still require saved validation.`
      : detail
        ? 'Inspect the saved layout and task-form selection for compatibility.'
        : 'Add the detail page for the selected task.',
  });
  return items;
}

function uniqueNode(document: WorkflowDefinitionDocument, id: string) {
  const matches = document.nodes.filter((node) => node?.id === id);
  return matches.length === 1 ? matches[0] : null;
}

function sameReference(
  left: WorkflowCapabilityReference | null | undefined,
  right: WorkflowCapabilityReference | null | undefined,
) {
  return (
    !!left &&
    !!right &&
    left.key?.provider === right.key?.provider &&
    left.key?.kind === right.key?.kind &&
    left.key?.code === right.key?.code &&
    left.key?.version === right.key?.version &&
    left.contractHash === right.contractHash &&
    left.inputSchemaHash === right.inputSchemaHash &&
    left.outputSchemaHash === right.outputSchemaHash
  );
}

function name(node: WorkflowDefinitionNode) {
  return node.displayName || node.stepCode || node.id;
}
function label(code: string) {
  return code
    .replace(/[_-]/g, ' ')
    .replace(/^./, (letter) => letter.toUpperCase());
}
